// features/invoice/invoice.service.ts
import axios from 'axios';
import { get, post, put } from '../../api/api.service';
import { Freelancer } from '../../api/customer';

// Create axios instance with proper configuration for preliminary invoice
const createPreliminaryInvoiceClient = () => {
  const instance = axios.create({
    baseURL: (import.meta as any).env.VITE_API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add auth token interceptor
  instance.interceptors.request.use(config => {
    if (!config.headers) {
      config.headers = {};
    }
    
    let token = '';
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      token = userData.token || 
              localStorage.getItem('token') || 
              localStorage.getItem('authToken') || '';
    } catch (e) {
      token = localStorage.getItem('token') || 
              localStorage.getItem('authToken') || '';
    }
    
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  return instance;
};

/* --------------------------------------------------------------------------
 *                               Bank Details
 * -------------------------------------------------------------------------- */

export interface BankDetails {
  id: number;
  userId: number;
  signUpUser: {
    organizationName: string;
  } | null;
  bank: string;
  bicNumber: string;
  ibanNumber: string;
  accountNumber: string;
  clearingNumber: string;
  incomeTax: number;
  fakturaFee: number;
  saveHolidayPay: boolean;
  salaryInfoViaSms: boolean;
  salaryInfoViaEmail: boolean;
  createdBy: number;
  createdDate: string;
  updatedBy: number;
  updatedDate: string;
  isDeleted: boolean;
}

export interface BankDetailsResponse {
  statusCode: number;
  message: string;
  response: BankDetails[];
  totalCount: number;
  numberOfPages: number;
}

export const getBankDetailsApi = async (): Promise<BankDetails[]> => {
  try {
    const response: BankDetailsResponse = await get('/Settings');

    if (response.statusCode === 200) {
      return response.response;
    } else {
      throw new Error(response.message || 'Failed to fetch bank details');
    }
  } catch (error) {
    throw error;
  }
};

/* --------------------------------------------------------------------------
 *                                  OTP
 * -------------------------------------------------------------------------- */

export interface OTPGenerationRequest {
  type: number;
  email: string;
  lang: string;
}

export interface OTPGenerationResponse {
  statusCode: number;
  message: string;
}

export const generateOTPApi = async (
  lang: string = 'en'
): Promise<OTPGenerationResponse> => {
  try {
    const body: OTPGenerationRequest = {
      type: 3,
      email: '',
      lang,
    };

    const response = await post<OTPGenerationResponse>('/settings/generate-otp', body);
    return response;
  } catch (error) {
    throw error;
  }
};

/* --------------------------------------------------------------------------
 *                          Save Bank Details
 * -------------------------------------------------------------------------- */

export interface SaveBankDetailsRequest {
  id?: number;
  bank: string;
  accountNumber?: string;
  clearingNumber?: string;
  ibanNumber?: string;
  bicNumber?: string;
  incomeTax?: number;
  code: string; // OTP code
}

export interface SaveBankDetailsResponse {
  statusCode: number;
  message: string;
}

export const saveBankDetailsApi = async (
  payload: SaveBankDetailsRequest
): Promise<SaveBankDetailsResponse> => {
  try {
    const response = await put<SaveBankDetailsResponse>('/settings/save-customer-salary', payload);
    return response;
  } catch (error) {
    throw error;
  }
};

/* --------------------------------------------------------------------------
 *                      Invoice Calculations (Working Hours + Invoice)
 * -------------------------------------------------------------------------- */

export interface WorkingHour {
  id: number;
  workingDate: string;
  dateId: string;
  number: number;
  unit: string;
  vat: number;
  isArticleShownInInvoice: boolean;
  amountPerUnit: number;
  amount: number;
  articleNumber: string;
  workingDescription: string;
  houseworkType: string;
  workingHour: number;
  timeSheetWorkingHourId?: number; // Timesheet reference ID (0 if not from timesheet)

  // Optional fields
  amountPerUnitSek?: number;
  isTaxFreeArticle?: boolean;
  isPdfAttachedToInvoice?: boolean;
  tripStartedPlace?: string;
  tripEndedPlace?: string;
  distance?: number;
  country?: string;
  allowancePerDay?: number;
  reasonForTrip?: string;
  carRegistrationNo?: string;
  tripStartedDate?: string;
  tripEndedDate?: string;
  totalDayOfTravels?: number;
  allowanceDestination?: string;
  expenseCategory?: string;
  fileName?: string;
  fileContent?: string;
  amountInSek?: number;
  isHalfDay?: boolean | string; // Can be boolean or string ("0" or "1")
  foodAllowance?: number;
  articleId?: number;
}

export interface InvoiceCalculationsPayload {
  vat: number;
  travel: number;
  allowance: number;
  isPreliminaryInvoice: boolean;
  pension?: boolean | number;
  amount: number;
  rutDeduction: boolean;
  rootDeduction: boolean;
  expressPayout: boolean;
  holidaySavings?: boolean | number;
  pensionFee: number;
  pensionAmount: number;
  tax: number;
  fee: number;
  expenditureForMaterialsAndToolsTaxFreeAmount: number;
  HourlyWagesAmount: number;
  rutTaxValue: number;
  socialFee: number;
  rootTaxValue: number;
  expressFee: number;
  holidaySavingsFee: number;
  currencyRate: string;
  currencyName: string;
  invoiceType?: number;
  numberOfFreelancers: number;
  workingHours: WorkingHour[];
  customerId: number;
  createdDate: string | Date;
  description?: string; // Invoice description/customer notes
  email?: string; // Customer email for invoice
  invoiceNumber?: number; // Invoice number from URL for edit mode
}

export interface TotalCalculationBreakdown
{
  freelancerData: InvoiceCalculationsResponse[];
  totalCalculation: InvoiceCalculationsResponse;
  

}

export interface InvoiceCalculationsResponse {
  response?: {
    amountIncludingVat: number;
    amount: number;
    net: number;
    tax: number;
    pensionPercentage: number;
    pension: number;
    holidayPayAmount: number;
    holidayPayAmountInSek: number;
    expressFee: number;
    expressFeeInSek: number;
    rutDeductionAmount: number;
    rootDeductionAmount: number;
    taxInSek: number;
    netInSek: number;
    workingHours?: any[];
  };

  // Legacy format support from older backend versions
  success?: boolean;
  data?: {
    totalAmount: number;
    netAmount: number;
    taxAmount: number;
    pensionAmount: number;
    holidaySavingsAmount: number;
    expressPayoutFee: number;
    rutDeductionAmount: number;
    rotDeductionAmount: number;
  };

  message?: string;
}

export const calculateInvoiceApi = async (
  payload: InvoiceCalculationsPayload,
  signal?: AbortSignal
): Promise<TotalCalculationBreakdown> => {
  try {
    const response = await post<TotalCalculationBreakdown>(
      '/invoicetoolv2/total-calculation',
      payload,
      { signal }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/* --------------------------------------------------------------------------
 *                          Create Invoice
 * -------------------------------------------------------------------------- */

export interface CreateInvoicePayload {
  approvalInvoiceId: number;
  rootDeductionAmount?: number;
  rutDeductionAmount?: number;
  invoiceId: number;
  sendInvoice: number;
  vat: number;
  pension: boolean;
  amount: number;
  isFixed: boolean;
  currency: string;
  brfNo?: string;
  registerAssignment: boolean;
  showDates: boolean;
  house: boolean;
  holidaySavings: boolean;
  description: string;
  taxValue: number;
  totalToPay: number;
  rutDeduction: boolean;
  rootDeduction: boolean;
  file?: any;
  expressPayout: boolean;
  expressFee?: number;
  workingHours: any[];
  email: string;
  apartment: boolean;
  pensionFee: number;
  holidaySavingsFee: number;
  tax: number;
  rotApartmentNo?: string;
  pensionAmount: number;
  customerId: number;
  expenditureForMaterialsAndToolsTaxFreeAmount: number;
  holidaySavingsAmount?: number;
  amountIncVat: number;
  secondaryEmailAddress?: string;
  multipleFreelancer: boolean;
  currencyRate: string | number;
  invoiceFreelancer?: any[];
  locale?: string;
  createdDate?: string;
  dueDate?: string;  // NEW - Due date field
  draftId?: string;  // Draft invoice ID from URL
  isDraft?: boolean;  // Whether this is a draft invoice
}

export interface CreateInvoiceResponse {
  statusCode: number;
  message: string;
  response: {
    invoiceNumber?: string;
    isAlreadySynced?: boolean;
    pdfData?: string;
    [key: string]: any;
  };
}

export const createInvoiceApi = async (
  payload: CreateInvoicePayload
): Promise<CreateInvoiceResponse> => {
  try {
    const response = await post<CreateInvoiceResponse>(
      '/invoicetoolv2/new-add-invoice',
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// Validates a single freelancer's identity/SSN verification status before they
// are added to an invoice. Resolves on 200 (verified); the backend returns 400
// with a message when the freelancer is not identity-verified.
export const validateFreelancerIdentityApi = async (userId: number) => {
  try {
    const response = await post('/invoicetoolv2/validate-freelancer-identity', {
      userId,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// export const createPreliminaryInvoiceApi = async (
//   payload: CreateInvoicePayload
// ): Promise<CreateInvoiceResponse> => {
//   try {
//     const response = await post<CreateInvoiceResponse>(
//       '/invoice/preliminary-invoice',
//       payload
//     );
    
//     console.log('Preliminary Invoice Response:', response);
//     return response;
//   } catch (error) {
//     throw error;
//   } 
// };
export const createPreliminaryInvoiceApi = async (
  payload: CreateInvoicePayload
): Promise<Blob> => {
  try { 
    // Ensure file is properly formatted as an array
    let fileArray: any[] = [];
    
    if (payload.file) {
      if (Array.isArray(payload.file)) {
        // If it's already an array, map it to ensure proper structure
        fileArray = payload.file.map((f: any) => ({
          fileName: f.fileName || f.name || '',
          fileContent: f.fileContent || f.content || ''
        }));
      } else if (typeof payload.file === 'object') {
        // If it's an object, check if it has file property
        if (payload.file.file && Array.isArray(payload.file.file)) {
          fileArray = payload.file.file.map((f: any) => ({
            fileName: f.fileName || f.name || '',
            fileContent: f.fileContent || f.content || ''
          }));
        } else if (payload.file.fileName || payload.file.fileContent) {
          // Single file object
          fileArray = [{
            fileName: payload.file.fileName || '',
            fileContent: payload.file.fileContent || ''
          }];
        }
      }
    }

    const cleanedPayload = {
      ...payload,
      file: fileArray
    };


    const apiClient = createPreliminaryInvoiceClient();
    const response = await apiClient.post("/draftinvoicev2/draft-invoice", cleanedPayload, {
      responseType: "blob", // 🔥 important for FileContentResult
      headers: {
        Accept: "application/pdf",
        "Content-Type": "application/json",
      },
    });
    
    
    return response.data; // this is the PDF Blob
  } catch (error) {
    console.error('❌ Preliminary Invoice API Error:', error);
    throw error;
  }
};

/* --------------------------------------------------------------------------
 *                          Create Draft Invoice
 * -------------------------------------------------------------------------- */

export interface CreateDraftInvoicePayload {
  invoicePdf: string; // base64 encoded PDF
  approvalInvoiceId: number;
  rootDeductionAmount?: number;
  rutDeductionAmount?: number;
  invoiceId: number;
  sendInvoice: number;
  vat: number;
  pension: boolean;
  amount: number;
  isFixed: boolean;
  currency: string;
  brfNo?: string;
  registerAssignment: boolean;
  showDates: boolean;
  house: boolean;
  holidaySavings: boolean;
  description: string;
  taxValue: number;
  totalToPay: number;
  rutDeduction: boolean;
  rootDeduction: boolean;
  file?: any;
  expressPayout: boolean;
  expressFee?: number;
  workingHours: any[];
  email: string;
  apartment: boolean;
  pensionFee: number;
  holidaySavingsFee: number;
  tax: number;
  rotApartmentNo?: string;
  pensionAmount: number;
  customerId: number;
  expenditureForMaterialsAndToolsTaxFreeAmount: number;
  holidaySavingsAmount?: number;
  amountIncVat: number;
  secondaryEmailAddress?: string;
  multipleFreelancer: boolean;
  currencyRate: string | number;
  invoiceFreelancer?: any[];
  locale?: string;
  dueDate?: string;  // NEW - Due date field
}

export interface CreateDraftInvoiceResponse {
  statusCode: number;
  message: string;
  response: {
    draftInvoiceId?: number;
    [key: string]: any;
  };
}

export const createDraftInvoiceApi = async (
  payload: CreateDraftInvoicePayload
): Promise<CreateDraftInvoiceResponse> => {
  try {
    // Extract locale from payload for query string
    const locale = payload.locale || 'en-us';
    
    const response = await post<CreateDraftInvoiceResponse>(
      `/DraftInvoiceV2/add-draft-invoice?lang=${locale}`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/* --------------------------------------------------------------------------
 *                          Get All Invoices (with pagination & search)
 * -------------------------------------------------------------------------- */

export interface InvoiceCustomer {
  customerId: number;
  name: string;
  orgNo: string;
  ssn: string;
  reference: string;
  phoneNumber: string;
  email: string;
  secondaryEmailAddress: string | null;
  isNotes: boolean;
  co: string | null;
  address: string;
  address2: string;
  city: string;
  zipCode: string;
  isActive: boolean;
  fortnoxCustomerId: string;
  country: string;
  vatNumber: string;
  paymentTerm: string;
  language: string;
  countryCode: string;
  isAddedOnFortnox: boolean;
  isDetailsUpdatedOnFortnox: boolean;
  isVATRegistred: boolean;
  accountType: number;
  invoiceType: number | null;
  hasAnyInvoice: boolean;
  customerAddress: any | null;
}

export interface InvoiceWorkingHour {
  id: number;
  invoiceId: number;
  workingDate: string;
  workingHour: number;
  unit?: number | string | null;
  amountPerUnit?: number | null;
  amount: number;
  amountInSek?: number | null;
  articleNumber?: string | null;
  articleName?: string | null;
  userId: number;
  [key: string]: any;
}

export interface InvoiceItem {
  id: number;
  invoiceNumber: number;
  fortnoxInvoiceNumber: number;
  amount: number;
  amountInSEK: number;
  userName: string;
  invoiceType: number;
  vat: number;
  vatAmount: number | null;
  amountIncVat: number;
  tax: number;
  customerId: number;
  customer: InvoiceCustomer;
  showDates: boolean;
  showHours: boolean;
  rotDeduction: boolean;
  rutDeduction: boolean;
  invoiceStatus: number;
  invoiceSalaryStatus: number;
  expressPayout: boolean;
  currency: string;
  currencyRate: number;
  multipleFreelancer: boolean;
  holidaySavings: boolean;
  pension: boolean;
  holidaySavingsAmount: number;
  holidaySavingsFee: number;
  pensionAmount: number;
  pensionFee: number;
  createdDate: string;
  dueDate: string;
  workingHours: InvoiceWorkingHour[];
  invoiceStageForUserView: number;
  invoicePurposeType: number;
  toPayAmont: number;
  /**
   * Latest SendGrid email-delivery status for this invoice, or null when no
   * email has been sent / no tracking exists yet. Raw lowercase SendGrid event
   * name (e.g. "delivered", "open", "bounce"). Mapped to a UI badge via
   * mapSendGridEmailStatus in common.helper.ts — never rendered raw.
   */
  emailStatus?: string | null;
  [key: string]: any;
}

export interface GetAllInvoicesParams {
  PageNumber: number;
  PageSize: number;
  SearchQuery?: string;
}

export interface GetAllInvoicesResponse {
  statusCode?: number;
  message?: string;
  response?: InvoiceItem[];
  totalCount?: number;
  // Direct array response
  data?: InvoiceItem[];
}

export const getAllInvoicesApi = async (
  params: GetAllInvoicesParams
): Promise<{ data: InvoiceItem[]; totalCount: number }> => {
  try {
    const queryParams = new URLSearchParams({
      PageNumber: params.PageNumber.toString(),
      PageSize: params.PageSize.toString(),
    });

    if (params.SearchQuery) {
      queryParams.append('SearchQuery', params.SearchQuery);
    }

    const response: any = await get(
      `/invoicev2/get-all-invoice-v2?${queryParams.toString()}`
    );

    // Handle both response formats
    if (Array.isArray(response)) {
      // Direct array response
      return {
        data: response as InvoiceItem[],
        totalCount: response.length,
      };
    } else if (response.response) {
      // Wrapped response
      return {
        data: response.response as InvoiceItem[],
        totalCount: response.totalCount || response.response.length,
      };
    } else if (response.data) {
      // Alternative wrapped format
      return {
        data: response.data as InvoiceItem[],
        totalCount: response.totalCount || response.data.length,
      };
    }

    return {
      data: [],
      totalCount: 0,
    };
  } catch (error) {
    throw error;
  }
};

/* --------------------------------------------------------------------------
 *                    Get Invoice Detail for Edit (NEW)
 * -------------------------------------------------------------------------- */

export interface InvoiceDetailForEditResponse {
  statusCode?: number;
  message?: string;
  response?: InvoiceItem;
  totalCount?: number;
  numberOfPages?: number;
}

export const getInvoiceDetailForEditApi = async (
  invoiceNumber: string
): Promise<InvoiceItem> => {
  try {
    const response: InvoiceDetailForEditResponse = await get(
      `/invoicev2/${invoiceNumber}`
    );

    // Handle response format
    if (response.response) {
      return response.response;
    } else if (response as any) {
      // Direct response without wrapper
      return response as any as InvoiceItem;
    }

    throw new Error('Invalid response format');
  } catch (error) {
    throw error;
  }
};

/* --------------------------------------------------------------------------
 *                    Get Draft Invoice Detail (NEW)
 * -------------------------------------------------------------------------- */

export const getDraftInvoiceDetailApi = async (
  invoiceId: string,
  lang: string = 'en-us'
): Promise<InvoiceItem> => {
  try {
    const response: InvoiceDetailForEditResponse = await get(
      `/DraftInvoiceV2/get-draft-invoice/${invoiceId}`
    );

    // Handle response format (same as getInvoiceDetailForEditApi)
    if (response.response) {
      return response.response;
    } else if (response as any) {
      // Direct response without wrapper
      return response as any as InvoiceItem;
    }

    throw new Error('Invalid response format');
  } catch (error) {
    throw error;
  }
};

/* --------------------------------------------------------------------------
 *                          Total Calculation
 * -------------------------------------------------------------------------- */

export interface TotalCalculationResponse {
  invoiceFreelancer: boolean;
  workingHours: boolean;
  amount: undefined;
  statusCode: number;
  message: string;
  response: {
    breakdown: {
      grossSalary: number;
      pensionAmount: number;
      holidaySavingsAmount: number;
      deductions: number;
      taxableAmount: number;
      tax: number;
      netSalary: number;
      vat: number;
      totalAmount: number;
    };
    details: any;
  };
}

export const getTotalCalculationApi = async (
  payload: CreateInvoicePayload
): Promise<TotalCalculationResponse> => {
  try {
    const response = await post<TotalCalculationResponse>(
      '/invoicetoolv2/total-calculation',
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};
